import React, { useState } from 'react';
import Button from 'components/Button/Button';
import { submitQuote as submitQuoteRequest } from 'api/website';
import {
	Form,
	Fieldset,
	Legend,
	FieldGrid,
	Field,
	Label,
	Required,
	Input,
	Textarea,
	Select,
	ErrorText,
	Actions,
	Note,
	Sent,
	SentTitle,
	SentText,
} from './styled';

/**
 * Where quote requests land. Change this and nothing else if the address moves.
 */
const QUOTE_EMAIL = 'ergilaze@gmail.com';

/**
 * Kept deliberately short. Storage limits, history retention, hosting region
 * and SLA all move the price, but they are our problem to work out from scope
 * and size — asking a reader to pick a gigabyte figure loses more completed
 * forms than the precision is worth. Anything genuinely unusual arrives in the
 * notes field, and the rest we ask on the call.
 *
 * Groups render in order. Field types: text, email, select, textarea.
 */
const GROUPS = [
	{
		legend: 'About you',
		fields: [
			{
				name: 'name',
				label: 'Name',
				type: 'text',
				required: true,
				placeholder: 'Jane Doe',
			},
			{
				name: 'email',
				label: 'Work email',
				type: 'email',
				required: true,
				placeholder: 'jane.doe@university.ac.uk',
			},
			{
				name: 'institution',
				label: 'Institution',
				type: 'text',
				required: true,
				wide: true,
				placeholder: 'University of Example',
			},
		],
	},
	{
		legend: 'Your deployment',
		fields: [
			{
				name: 'scope',
				label: 'Who would use Flass?',
				type: 'select',
				required: true,
				options: [
					'One department or school',
					'A faculty — several departments',
					'The whole institution',
					'Not decided yet',
				],
			},
			{
				name: 'size',
				label: 'Roughly how many people',
				type: 'select',
				required: true,
				options: [
					'Up to 100',
					'100 to 500',
					'500 to 1,000',
					'1,000 to 5,000',
					'More than 5,000',
				],
			},
			{
				name: 'start',
				label: 'When would you like to start?',
				type: 'select',
				options: [
					'This term',
					'Next term',
					'Next academic year',
					'Exploring options',
				],
			},
			{
				name: 'notes',
				label: 'Anything else we should know?',
				type: 'textarea',
				wide: true,
				placeholder:
					'A pilot you have in mind, systems Flass would need to connect to, procurement deadlines…',
			},
		],
	},
];

const ALL_FIELDS = GROUPS.reduce((acc, g) => acc.concat(g.fields), []);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildInitialValues = () =>
	ALL_FIELDS.reduce((acc, field) => {
		acc[field.name] = '';
		return acc;
	}, {});

const validate = (values) => {
	const errors = {};

	ALL_FIELDS.forEach((field) => {
		if (field.required && !values[field.name]) {
			errors[field.name] = 'Required';
		}
	});

	if (values.email && !EMAIL_PATTERN.test(values.email)) {
		errors.email = 'Enter a valid email address';
	}

	return errors;
};

/** Plain-text summary — readable in any mail client, no formatting to strip. */
const buildBody = (values) =>
	GROUPS.map((group) => {
		const lines = group.fields
			.filter((field) => values[field.name])
			.map((field) => `${field.label}: ${values[field.name]}`);

		if (!lines.length) return null;
		return `${group.legend.toUpperCase()}\n${lines.join('\n')}`;
	})
		.filter(Boolean)
		.join('\n\n');

/**
 * Fallback for when the API request fails — a mailto: with the answers
 * pre-filled, so a submission is never fully lost to a network hiccup.
 */
const buildMailtoFallbackUrl = (values) => {
	const subject = `Flass quote request — ${values.institution}`;
	return `mailto:${QUOTE_EMAIL}?subject=${encodeURIComponent(
		subject,
	)}&body=${encodeURIComponent(buildBody(values))}`;
};

const QuoteForm = () => {
	const [values, setValues] = useState(buildInitialValues);
	const [errors, setErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(false);
	const [sent, setSent] = useState(false);

	const setValue = (name, value) => {
		setValues((current) => ({ ...current, [name]: value }));
		// Clear the error as soon as the field is touched again, rather than
		// making the reader re-submit to find out whether they fixed it.
		setErrors((current) => {
			if (!current[name]) return current;
			const next = { ...current };
			delete next[name];
			return next;
		});
	};

	const onSubmit = async (event) => {
		event.preventDefault();
		const found = validate(values);
		setErrors(found);

		const firstInvalid = ALL_FIELDS.find((field) => found[field.name]);
		if (firstInvalid) {
			const node = document.getElementById(`quote-${firstInvalid.name}`);
			if (node) node.focus();
			return;
		}

		setSubmitting(true);
		setSubmitError(false);
		try {
			await submitQuoteRequest(values);
			setSent(true);
		} catch (err) {
			setSubmitError(true);
		} finally {
			setSubmitting(false);
		}
	};

	if (sent) {
		return (
			<Sent>
				<SentTitle>Thanks — that is everything we need.</SentTitle>
				<SentText>
					We will come back with a price, usually within two working
					days.
				</SentText>
			</Sent>
		);
	}

	return (
		<Form onSubmit={onSubmit} noValidate>
			{GROUPS.map((group) => (
				<Fieldset key={group.legend}>
					<Legend>{group.legend}</Legend>
					<FieldGrid>
						{group.fields.map((field) => {
							const id = `quote-${field.name}`;
							const error = errors[field.name];

							return (
								<Field key={field.name} $wide={field.wide}>
									<Label htmlFor={id}>
										{field.label}
										{field.required ? (
											<Required>*</Required>
										) : null}
									</Label>

									{field.type === 'select' ? (
										<Select
											id={id}
											$error={!!error}
											value={values[field.name]}
											onChange={(e) =>
												setValue(
													field.name,
													e.target.value,
												)
											}>
											<option value=''>
												Please choose
											</option>
											{field.options.map((option) => (
												<option
													key={option}
													value={option}>
													{option}
												</option>
											))}
										</Select>
									) : null}

									{field.type === 'textarea' ? (
										<Textarea
											id={id}
											placeholder={field.placeholder}
											value={values[field.name]}
											onChange={(e) =>
												setValue(
													field.name,
													e.target.value,
												)
											}
										/>
									) : null}

									{field.type === 'text' ||
									field.type === 'email' ? (
										<Input
											id={id}
											type={field.type}
											$error={!!error}
											placeholder={field.placeholder}
											value={values[field.name]}
											onChange={(e) =>
												setValue(
													field.name,
													e.target.value,
												)
											}
										/>
									) : null}

									{error ? <ErrorText>{error}</ErrorText> : null}
								</Field>
							);
						})}
					</FieldGrid>
				</Fieldset>
			))}

			{submitError ? (
				<ErrorText style={{ marginBottom: 14 }}>
					Could not send your request. Please try again, or{' '}
					<a href={buildMailtoFallbackUrl(values)}>
						email us directly
					</a>
					.
				</ErrorText>
			) : null}

			<Actions>
				<Button type='submit' size='medium' disabled={submitting}>
					{submitting ? 'Sending…' : 'Send request'}
				</Button>
			</Actions>

			<Note>
				We use these answers to price your deployment and nothing else.
				No newsletter, and we will not pass your details on.
			</Note>
		</Form>
	);
};

export default QuoteForm;
